/**
 * After submit: the customer-facing quote and, when options differ, a
 * picker for which inventory item fills each line. Add-to-cart and
 * download honor the RFQ configuration flags.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { reopenInCart } from "../quotes/reopen";
import { downloadQuotePdf } from "../quotes/api";
import { CartItem } from "../common/models/cart";
import {
  BuyerLineOptions,
  RfqInlineQuote,
  RfqLine,
  confirmRfqQuote,
  messageOf,
} from "./api";

const money = (n: number, currency = "USD") =>
  `${currency === "USD" ? "$" : `${currency} `}${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const leadTimeText = (min: number | null, max: number | null): string => {
  if (min && max && min !== max) return `${min}–${max} days`;
  const single = max || min;
  return single ? `${single} day${single === 1 ? "" : "s"}` : "";
};

type Props = {
  rfqId: string;
  lines: RfqLine[];
  options: BuyerLineOptions[];
  quote: RfqInlineQuote | null;
  showOptions: boolean;
  allowAddToCart: boolean;
  allowSaveQuote: boolean;
  onQuote: (quote: RfqInlineQuote) => void;
  onLines: (lines: RfqLine[]) => void;
  onError: (message: string) => void;
};

export default function RfqQuotePanel({
  rfqId,
  lines,
  options,
  quote,
  showOptions,
  allowAddToCart,
  allowSaveQuote,
  onQuote,
  onLines,
  onError,
}: Props) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const preferences = (): { [lineId: string]: string } => {
    const next: { [lineId: string]: string } = {};
    for (const line of lines) {
      if (line.preferredProductId) next[line.id] = line.preferredProductId;
    }
    return next;
  };

  const pick = (lineId: string, productId: string) => {
    onLines(
      lines.map((line) =>
        line.id === lineId ? { ...line, preferredProductId: productId } : line,
      ),
    );
  };

  const confirm = async () => {
    setSaving(true);
    try {
      const result = await confirmRfqQuote(rfqId, preferences());
      onQuote(result.quote);
    } catch (e) {
      onError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  const addToCart = async () => {
    let current = quote;
    if (!current) {
      setSaving(true);
      try {
        const result = await confirmRfqQuote(rfqId, preferences());
        current = result.quote;
        onQuote(result.quote);
      } catch (e) {
        onError(messageOf(e));
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }
    reopenInCart({
      id: current.id,
      reference: current.reference,
      cartItems: current.cartItems as CartItem[],
    });
    navigate("/cart");
  };

  const download = async () => {
    if (!quote) return;
    setDownloading(true);
    try {
      await downloadQuotePdf({ id: quote.id, reference: quote.reference });
    } catch (e) {
      onError(messageOf(e));
    } finally {
      setDownloading(false);
    }
  };

  if (!options.length && !quote) return null;

  return (
    <div className="mt-4 rounded-xl border border-green-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-[#00062e]">
        {quote ? `Quote ${quote.reference}` : "Choose how we fill each item"}
      </h3>
      {quote && (
        <p className="mt-1 text-sm text-gray-700">
          {money(quote.totals.totalPrice, quote.totals.currency)}
          {quote.totals.shippingTotal
            ? ` including ${money(
                quote.totals.shippingTotal,
                quote.totals.currency,
              )} shipping`
            : ""}
          .{" "}
          <Link
            className="font-medium text-blue-700 hover:underline"
            to={`/quotes/${quote.id}`}
          >
            Open quote
          </Link>
        </p>
      )}

      {showOptions &&
        !quote &&
        options.map((entry) => {
          const line = lines.find((l) => l.id === entry.lineId);
          const label = line
            ? [line.material, line.shape].filter(Boolean).join(" ") ||
              "Requested item"
            : "Requested item";
          const chosen =
            line?.preferredProductId ||
            entry.options.find((o) => o.selected)?.productId ||
            "";
          if (!entry.options.length) return null;
          return (
            <div key={entry.lineId} className="mt-3">
              <p className="text-xs font-semibold text-[#00062e]">{label}</p>
              <div className="mt-1 flex flex-col gap-2">
                {entry.options.map((option) => {
                  const isChosen = option.productId === chosen;
                  return (
                    <label
                      key={option.productId}
                      className={`flex cursor-pointer items-start gap-2 rounded-lg border p-2.5 ${
                        isChosen
                          ? "border-teal-500 bg-teal-50/50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="radio"
                        className="mt-1"
                        name={`buyer-opt-${entry.lineId}`}
                        checked={isChosen}
                        onChange={() => pick(entry.lineId, option.productId)}
                      />
                      <span className="text-sm text-gray-800">
                        <span className="font-medium">
                          {option.summary || "Inventory item"}
                        </span>
                        {option.lineAmount != null
                          ? ` · ${money(option.lineAmount)}`
                          : option.price
                            ? ` · ${money(option.price.value)}/${
                                option.price.unit || "ea"
                              }`
                            : ""}
                        {leadTimeText(
                          option.leadTimeDaysMin,
                          option.leadTimeDaysMax,
                        )
                          ? ` · ${leadTimeText(
                              option.leadTimeDaysMin,
                              option.leadTimeDaysMax,
                            )}`
                          : ""}
                        {!option.exactMatch && option.differences.length ? (
                          <span className="mt-0.5 block text-xs text-amber-800">
                            {option.differences.join("; ")}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}

      <div className="mt-3 flex flex-wrap gap-2">
        {!quote && (
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={saving}
            className="rounded-lg bg-[#00062e] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#001858] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save quote"}
          </button>
        )}
        {allowAddToCart && (
          <button
            type="button"
            onClick={() => void addToCart()}
            disabled={saving}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-[#00062e] hover:bg-gray-50 disabled:opacity-50"
          >
            Add to cart
          </button>
        )}
        {allowSaveQuote && quote && (
          <button
            type="button"
            onClick={() => void download()}
            disabled={downloading}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-[#00062e] hover:bg-gray-50 disabled:opacity-50"
          >
            {downloading ? "Downloading…" : "Download PDF"}
          </button>
        )}
      </div>
    </div>
  );
}
